import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Folder, FolderOpen, CaretRight, CaretLeft, X, Check } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modal folder browser for Dropbox. Lets admin drill into sub-folders and
// pick one — no typing required. Uses GET /api/dropbox/browse?path=... which
// returns folders directly under a path (non-recursive).
const DropboxFolderPicker = ({ open, initialPath = '', onCancel, onSelect }) => {
  const [path, setPath] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPath = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/dropbox/browse`, { params: { path: p } });
      setPath(data.path || '');
      setEntries(data.entries || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load folder contents.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    // Start at the parent of `initialPath` when available, otherwise at root
    // so admins see where their last folder lived without typing.
    const start = initialPath && initialPath.includes('/')
      ? initialPath.substring(0, initialPath.lastIndexOf('/'))
      : '';
    loadPath(start || '');
  }, [open, initialPath, loadPath]);

  if (!open) return null;

  // Breadcrumb segments. Root is represented by ''.
  const segments = path ? path.replace(/^\//, '').split('/') : [];
  const crumbs = [{ label: 'Dropbox', path: '' }];
  segments.forEach((seg, i) => {
    crumbs.push({ label: seg, path: '/' + segments.slice(0, i + 1).join('/') });
  });

  const goUp = () => {
    if (!path) return;
    const parent = path.substring(0, path.lastIndexOf('/'));
    loadPath(parent);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onCancel}
      data-testid="dropbox-folder-picker-backdrop"
    >
      <div
        className="bg-surface border border-border-color rounded-xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="dropbox-folder-picker"
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-bold font-heading text-gold flex items-center gap-2">
            <FolderOpen size={20} weight="duotone" /> Pick a Dropbox folder
          </h3>
          <button
            onClick={onCancel}
            aria-label="Close"
            data-testid="dropbox-folder-picker-close"
            className="p-1 text-text-secondary hover:text-gold"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Breadcrumbs */}
        <div className="text-xs mb-3 flex flex-wrap items-center gap-1" data-testid="dropbox-folder-picker-crumbs">
          {crumbs.map((c, i) => (
            <React.Fragment key={c.path || 'root'}>
              {i > 0 && <CaretRight size={10} weight="bold" className="text-text-secondary" />}
              <button
                type="button"
                onClick={() => loadPath(c.path)}
                className={`hover:text-gold ${i === crumbs.length - 1 ? 'text-gold font-semibold' : 'text-text-secondary'}`}
                data-testid={`dropbox-folder-picker-crumb-${i}`}
              >
                {c.label}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Up button + list */}
        <div className="border border-border-color rounded-lg bg-black/40 max-h-72 overflow-y-auto" data-testid="dropbox-folder-picker-list">
          {path && (
            <button
              type="button"
              onClick={goUp}
              data-testid="dropbox-folder-picker-up"
              className="w-full text-left px-3 py-2 text-xs text-text-secondary hover:bg-gold/10 hover:text-gold flex items-center gap-2 border-b border-border-color"
            >
              <CaretLeft size={12} weight="bold" /> Up one level
            </button>
          )}
          {loading && (
            <p className="px-3 py-4 text-xs text-text-secondary" data-testid="dropbox-folder-picker-loading">Loading…</p>
          )}
          {!loading && error && (
            <p className="px-3 py-4 text-xs text-danger" data-testid="dropbox-folder-picker-error">{error}</p>
          )}
          {!loading && !error && entries.length === 0 && (
            <p className="px-3 py-4 text-xs text-text-secondary" data-testid="dropbox-folder-picker-empty">
              No sub-folders here. Use <span className="text-gold">Select this folder</span> to sync images from this level.
            </p>
          )}
          {!loading && !error && entries.map((f) => (
            <button
              key={f.pathLower}
              type="button"
              onClick={() => loadPath(f.pathDisplay)}
              data-testid={`dropbox-folder-picker-entry-${f.name}`}
              className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-gold/10 hover:text-gold flex items-center gap-2 border-b border-border-color/50 last:border-b-0"
            >
              <Folder size={14} weight="duotone" className="text-gold shrink-0" />
              <span className="truncate">{f.name}</span>
              <CaretRight size={12} weight="bold" className="ml-auto text-text-secondary" />
            </button>
          ))}
        </div>

        <div className="mt-3 text-[11px] text-text-secondary">
          Current folder: <span className="font-mono text-text-primary">{path || '/ (whole Dropbox)'}</span>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="dropbox-folder-picker-cancel"
            className="px-4 py-2 border border-border-color text-text-secondary rounded-lg text-sm hover:border-gold hover:text-gold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSelect(path)}
            data-testid="dropbox-folder-picker-select"
            disabled={loading || !!error}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-black font-bold rounded-lg text-sm hover:bg-gold-hover disabled:opacity-50"
          >
            <Check size={14} weight="bold" /> Select this folder
          </button>
        </div>
      </div>
    </div>
  );
};

export default DropboxFolderPicker;
